import { useCallback } from "react";
import { useAppStore } from "./useAppStore";

const NO_HIDDEN: string[] = [];

/**
 * Per-user, per-table column visibility backed by the persisted store.
 * Keyed by the REAL logged-in user (not the impersonated one) — column
 * preferences belong to the person at the keyboard.
 * With an undefined tableId, returns an inert contract (no reads, no-ops).
 */
export const useTableColumnVisibility = (tableId: string | undefined) => {
  const userId = useAppStore((state) => state.user?.userId);
  const key = tableId ? `${userId ?? "anon"}:${tableId}` : undefined;

  const hiddenColumnIds =
    useAppStore((state) =>
      key ? state.hiddenColumnsByTable[key] : undefined,
    ) ?? NO_HIDDEN;

  const toggleHiddenColumn = useAppStore.use.toggleHiddenColumn();
  const resetHiddenColumns = useAppStore.use.resetHiddenColumns();

  const toggleColumn = useCallback(
    (columnId: string) => {
      if (key) {
        toggleHiddenColumn(key, columnId);
      }
    },
    [key, toggleHiddenColumn],
  );

  const resetColumns = useCallback(() => {
    if (key) {
      resetHiddenColumns(key);
    }
  }, [key, resetHiddenColumns]);

  const isHidden = useCallback(
    (columnId: string) => hiddenColumnIds.includes(columnId),
    [hiddenColumnIds],
  );

  return { hiddenColumnIds, isHidden, toggleColumn, resetColumns };
};
